/* Where the watermark goes, and what it is made of.
 *
 * Here rather than in the component for the same reason as lib/overlay.js:
 * tests/test_palette.py forbids a raw hex in a .vue so the interface follows
 * the theme, and these are the opposite of that. A white watermark has to stay
 * white on a light page, because it is drawn onto the visitor's picture rather
 * than painted on ours.
 *
 * `key` names a catalogue entry so the label is translated; the value is what
 * usableWatermark() in imageTransform.js validates.
 */

export const WATERMARK_POSITIONS = [
	{key: "bottomRight", value: "bottom-right"},
	{key: "bottomLeft", value: "bottom-left"},
	{key: "topRight", value: "top-right"},
	{key: "topLeft", value: "top-left"},
	{key: "centre", value: "centre"},
	{key: "diagonal", value: "diagonal"},
	{key: "tiled", value: "tiled"},
]

export const WATERMARK_PLACEMENTS = WATERMARK_POSITIONS.map((p) => p.value)

/* White first: a watermark is usually laid over a photograph and white with a
 * dark outline reads on more pictures than anything else. */
export const WATERMARK_COLOURS = [
	{key: "white", hex: "#ffffff"},
	{key: "black", hex: "#000000"},
	{key: "navy", hex: "#1b2a4a"},
	{key: "plum", hex: "#4636b3"},
	{key: "rose", hex: "#b3365e"},
	{key: "sand", hex: "#c8a06a"},
	{key: "lightGreen", hex: "#90ee90"},
	{key: "yellow", hex: "#ffd60a"},
]

/* The faces on offer.
 *
 * `stack` is handed straight to canvas ctx.font and to the preview's CSS, so
 * the two cannot disagree. Every entry ends in a generic family on purpose:
 * the drawing happens on the visitor's own device, so a face they do not have
 * falls back rather than failing, and it falls back to something of the same
 * shape rather than to whatever the browser defaults to.
 *
 * Lato is here because the application already loads it -- index.html imports
 * it for the interface -- so it costs no extra request. It is also the one
 * entry that has to be WAITED for: canvas fillText() does not block on a
 * webfont, and asking for one that has not arrived draws in something else
 * with no error. ensureWatermarkFont() in imageTransform.js is that wait.
 *
 * The rest are faces the operating system already has. `name` is a proper noun
 * and is not translated.
 */
export const WATERMARK_FONTS = [
	{key: "sans",   name: "Helvetica",  stack: '"Helvetica Neue", Helvetica, Arial, sans-serif'},
	{key: "lato",   name: "Lato",       stack: 'Lato, "Noto Sans", sans-serif'},
	{key: "serif",  name: "Georgia",    stack: 'Georgia, "Times New Roman", Times, serif'},
	{key: "heavy",  name: "Impact",     stack: 'Impact, Haettenschweiler, "Arial Narrow Bold", sans-serif'},
	{key: "wide",   name: "Verdana",    stack: 'Verdana, Geneva, sans-serif'},
	{key: "round",  name: "Trebuchet",  stack: '"Trebuchet MS", Tahoma, sans-serif'},
	{key: "mono",   name: "Courier",    stack: '"Courier New", Courier, monospace'},
	{key: "script", name: "Brush Script", stack: '"Brush Script MT", "Segoe Script", "Bradley Hand", cursive'},
]

export const WATERMARK_FONT_KEYS = WATERMARK_FONTS.map((f) => f.key)
export const DEFAULT_WATERMARK_FONT = "sans"

/* The stack for a key, always something usable: an unknown key is the same
 * kind of untrusted input as everything else a task page can preset. */
export function fontStack(key) {
	const found = WATERMARK_FONTS.find((f) => f.key === key)
	return (found || WATERMARK_FONTS[0]).stack
}

/* One spelling of the CSS font shorthand, shared by the canvas, the preview
 * and the font-loading wait, so none of them can ask for a different face. */
export function fontSpec(px, key) {
	return `600 ${px}px ${fontStack(key)}`
}

/* The longest text worth drawing. Not a validation rule anybody will hit by
 * accident -- it stops a pasted essay from being rendered at 4% of the short
 * edge and taking the canvas with it. */
export const MAX_WATERMARK_CHARS = 120

/* As a fraction of the SHORT edge, so one setting works on a 400px thumbnail
 * and a 6000px photograph alike. A percentage of the width would put the same
 * words at wildly different sizes on a portrait and a landscape crop. */
export const MIN_WATERMARK_SIZE = 0.02
export const MAX_WATERMARK_SIZE = 0.20
export const DEFAULT_WATERMARK_SIZE = 0.06

/* A logo is judged as a shape rather than read as a line, so it wants more of
 * the picture than lettering does. Also a fraction of the SHORT edge: the
 * logo is fitted INTO a box of that side, keeping its own proportions. */
export const MIN_LOGO_SIZE = 0.05
export const MAX_LOGO_SIZE = 0.60
export const DEFAULT_LOGO_SIZE = 0.20
